import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { SolicitudCambioDatosDialog } from "@/components/admin/SolicitudCambioDatosDialog";
import {
  CambioDatosCancelado,
  detectarCambios,
  esErrorGuardiaComprador,
  hayCambios,
  leerDatosVigentes,
  sinCamposConAutorizacion,
  type CambiosDatos,
  type ResultadoSolicitud,
} from "@/lib/usuarios/cambio-datos-comprador";
import { esErrorCorreoComprador, type ResultadoCambioCorreo } from "@/lib/usuarios/cambio-correo";

interface Pendiente {
  persona: { id: number; nombre: string };
  cambios: CambiosDatos;
  resolve: (r: ResultadoSolicitud) => void;
  reject: (e: CambioDatosCancelado) => void;
}

/**
 * "Editar Comprador": el nombre, el correo y el teléfono pasan por autorización.
 *
 *   const autorizacion = useAutorizacionCambioComprador('/admin/compradores');
 *   // en la mutación, ANTES del UPDATE de la persona:
 *   const { directo, solicitud } = await autorizacion.interceptar(idPersona, payload);
 *   await supabase.from('personas').update(directo).eq('id', idPersona);
 *   // onSuccess: autorizacion.avisar(solicitud)   onError: if (esCancelacionCambioDatos(e)) return;
 *   // y renderizar {autorizacion.dialogo}
 *
 * Si hay cambios protegidos abre el diálogo de motivo + evidencia y espera a que se envíe la
 * solicitud; si el usuario lo cancela, rechaza con `CambioDatosCancelado` y no se guarda nada.
 * `directo` es el payload sin los campos protegidos.
 *
 * Pantallas donde la persona puede o no ser compradora (Prospectos, Clientes, CRM, oferta
 * nueva...) usan `guardar`: intenta el UPDATE completo y, solo si la guardia de la BD lo
 * rechaza (`trg_personas_guardia_datos_comprador`), abre el mismo diálogo y guarda el resto.
 * Así decide la BD, no una consulta a `compradores` que el RLS de dinero puede ocultar.
 *
 *   const { resultado, solicitud } = await autorizacion.guardar(idPersona, payload, (p) =>
 *     supabase.from('personas').update(p).eq('id', idPersona));
 *   if (resultado.error) throw resultado.error;
 *
 * Con correo por `useCambioCorreoPersona`, `guardarConCorreo` corre antes la edge function:
 * si la rechaza por ser comprador, el correo nuevo entra a la misma solicitud.
 */
export function useAutorizacionCambioComprador(vista: string) {
  const { user, profile } = useAuth();
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);

  const interceptar = useCallback(
    async <T extends Record<string, any>>(
      idPersona: number,
      payload: T,
    ): Promise<{ directo: T; solicitud: ResultadoSolicitud | null }> => {
      const directo = sinCamposConAutorizacion(payload);
      // Contra la BD, no contra lo que trae la pantalla (algunas cargan la persona incompleta).
      const vigentes = await leerDatosVigentes(idPersona);
      const cambios = detectarCambios(vigentes, payload);
      if (!hayCambios(cambios)) return { directo, solicitud: null };

      const nombre = vigentes.nombre_legal || payload.nombre_legal || `Persona ${idPersona}`;
      const solicitud = await new Promise<ResultadoSolicitud>((resolve, reject) =>
        setPendiente({ persona: { id: idPersona, nombre }, cambios, resolve, reject }),
      );
      return { directo, solicitud };
    },
    [],
  );

  const guardar = useCallback(
    async <T extends Record<string, any>, R extends { error: { code?: string; message?: string } | null }>(
      idPersona: number,
      payload: T,
      ejecutar: (p: T) => PromiseLike<R>,
      opciones: { esComprador?: boolean } = {},
    ): Promise<{ resultado: R; solicitud: ResultadoSolicitud | null }> => {
      if (!opciones.esComprador) {
        const resultado = await ejecutar(payload);
        if (!esErrorGuardiaComprador(resultado.error)) return { resultado, solicitud: null };
      }
      const { directo, solicitud } = await interceptar(idPersona, payload);
      return { resultado: await ejecutar(directo), solicitud };
    },
    [interceptar],
  );

  const guardarConCorreo = useCallback(
    async <T extends Record<string, any>, R extends { error: { code?: string; message?: string } | null }>(
      idPersona: number,
      payload: T,
      ejecutar: (p: T) => PromiseLike<R>,
      sincronizarCorreo: () => Promise<ResultadoCambioCorreo>,
    ): Promise<{ resultado: R; solicitud: ResultadoSolicitud | null; correo: ResultadoCambioCorreo | null }> => {
      let correo: ResultadoCambioCorreo | null = null;
      let esComprador = false;
      try {
        correo = await sincronizarCorreo();
      } catch (e) {
        if (!esErrorCorreoComprador(e)) throw e;
        esComprador = true;
      }
      const { resultado, solicitud } = await guardar(idPersona, payload, ejecutar, { esComprador });
      return { resultado, solicitud, correo };
    },
    [guardar],
  );

  const avisar = useCallback((solicitud: ResultadoSolicitud | null | undefined) => {
    if (!solicitud) return;
    toast.success(`Cambio enviado a revisión y aprobación (ticket #${solicitud.numero_ticket})`, {
      description: "El nombre, correo o teléfono se actualizarán cuando se apruebe en Personas > Autorizaciones de datos.",
      duration: 8000,
    });
  }, []);

  const dialogo = pendiente ? (
    <SolicitudCambioDatosDialog
      persona={pendiente.persona}
      cambios={pendiente.cambios}
      vista={vista}
      solicitante={profile?.nombre ?? user?.email ?? null}
      onEnviada={(r) => {
        pendiente.resolve(r);
        setPendiente(null);
      }}
      onCancelar={() => {
        pendiente.reject(new CambioDatosCancelado());
        setPendiente(null);
      }}
    />
  ) : null;

  return { interceptar, guardar, guardarConCorreo, avisar, dialogo };
}
